import { plainToInstance } from 'class-transformer';

import { useQueryClient } from '@tanstack/react-query';
import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AuthApi, PeopleApi } from '../../api';
import { RoutePath } from '../../config';
import { ApiResponse, AuthContext, User } from '../../model';
import type { SignInOptions } from '../../model';

const CLIENT_PROFILE_SETUP_KEY = 'pax_client_needs_profile_setup';

export const AuthContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();
  const { pathname: routerPathname } = useLocation();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [authorized, setAuthorized] = useState<boolean>(false);
  const [isClient, setIsClient] = useState<boolean>(true);
  const [userId, setUserId] = useState<number | null>(null);
  const [clientNeedsProfileSetup, setClientNeedsProfileSetup] =
    useState<boolean>(
      () => sessionStorage.getItem(CLIENT_PROFILE_SETUP_KEY) === '1',
    );
  const isClientRef = useRef<boolean>(true);
  const hasCheckedAuth = useRef<boolean>(false);

  useEffect(() => {
    // Предотвращаем повторные проверки при смене страницы, если уже проверили
    // Также защита от двойного вызова в React.StrictMode
    if (hasCheckedAuth.current) {
      return;
    }

    // Помечаем сразу, чтобы предотвратить повторный вызов в StrictMode
    hasCheckedAuth.current = true;

    const checkAuth = async () => {
      const currentPath = routerPathname;
      const isAuthPage =
        currentPath === RoutePath.ClientLogin ||
        currentPath === RoutePath.DoctorLogin ||
        currentPath === RoutePath.ClientRegister ||
        currentPath === RoutePath.Landing;

      // ВАЖНО: Если на странице авторизации, НИКОГДА не делаем запросы
      // Это должно быть первой проверкой
      if (isAuthPage) {
        setAuthorized(false);
        setIsLoading(false);
        return;
      }

      try {
        let response: ApiResponse<any>;
        try {
          response = await AuthApi.checkClientAuth();
          if (response.data) {
            setAuthorized(true);
            setIsClient(true);
            isClientRef.current = true;
            setUserId(response.data?.people_main_id || response.data?.id);
            setClientNeedsProfileSetup(
              sessionStorage.getItem(CLIENT_PROFILE_SETUP_KEY) === '1',
            );
            setIsLoading(false);

            const lastPath = sessionStorage.getItem('lastPath');
            if (lastPath && !isAuthPage) {
              navigate(lastPath);
              sessionStorage.removeItem('lastPath');
            }
            return;
          }
        } catch (clientError) {
          try {
            response = await AuthApi.checkAuth();
            if (response.status === 'success') {
              setAuthorized(true);
              setIsClient(response.data.role === 'client');
              isClientRef.current = response.data.role === 'client';
              setUserId(response.data.user ?? response.data.id);
              setIsLoading(false);

              const lastPath = sessionStorage.getItem('lastPath');
              if (lastPath && !isAuthPage) {
                navigate(lastPath);
                sessionStorage.removeItem('lastPath');
              }
              return;
            }
          } catch (doctorError) {
            setAuthorized(false);
            setIsLoading(false);
            return;
          }
        }
        setAuthorized(false);
        setIsLoading(false);
      } catch (error) {
        setAuthorized(false);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [routerPathname, navigate]);

  const markClientProfileComplete = useCallback(() => {
    sessionStorage.removeItem(CLIENT_PROFILE_SETUP_KEY);
    setClientNeedsProfileSetup(false);
  }, []);

  const signIn = useCallback(
    (userId: number, isClient: boolean, options?: SignInOptions) => {
      setAuthorized(true);
      setIsClient(isClient);
      setUserId(userId);
      isClientRef.current = isClient;
      sessionStorage.setItem('lastSignInAt', String(Date.now()));

      if (isClient) {
        if (options?.isProfileCompleted === false) {
          sessionStorage.setItem(CLIENT_PROFILE_SETUP_KEY, '1');
          setClientNeedsProfileSetup(true);
        } else if (options?.isProfileCompleted === true) {
          sessionStorage.removeItem(CLIENT_PROFILE_SETUP_KEY);
          setClientNeedsProfileSetup(false);
        }

        // Seed флагов согласия в кэш профиля — UI-гейт читает только useProfile.
        if (typeof options?.legal_agreement_accepted === 'boolean') {
          queryClient.setQueryData(
            [PeopleApi.KEY, 'me', true],
            (prev: User | undefined) =>
              plainToInstance(User, {
                ...(prev ?? {}),
                legal_agreement_accepted: options.legal_agreement_accepted,
                legal_agreement_version:
                  options.legal_agreement_version ?? null,
              }),
          );
        }
      }
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    await AuthApi.logOut();
    setAuthorized(false);
    setUserId(null);
    sessionStorage.removeItem(CLIENT_PROFILE_SETUP_KEY);
    setClientNeedsProfileSetup(false);
    hasCheckedAuth.current = false; // Сбрасываем флаг для возможности повторной проверки
    navigate(
      isClientRef.current ? RoutePath.ClientLogin : RoutePath.DoctorLogin,
    );
    queryClient.clear();
  }, [navigate, queryClient]);

  return (
    <AuthContext.Provider
      value={{
        isAuthorized: authorized,
        userId,
        isClient,
        isLoading,
        clientNeedsProfileSetup,
        signIn,
        markClientProfileComplete,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
